/**
 * Admin Catering CMS - Bulaccino Cafe
 * Manage catering menu items, orders, calendar, and export
 */
import { useState, useEffect, useMemo } from 'react';
import { 
  Plus, Pencil, Trash2, Save, X, ChevronDown, ChevronUp,
  Package, Users, FileText, Clock, Check, AlertCircle, Loader2,
  DollarSign, Search, TrendingUp, BarChart3, Calendar, Download,
  ChevronLeft, ChevronRight, CreditCard, Banknote, Mail, Image as ImageIcon,
  Eye, EyeOff, Upload, ToggleLeft, ToggleRight, Settings
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

// Category options - SYNCED WITH EXCEL "Catering Items List 2026 with Pricing"
const CATEGORIES = [
  { value: 'hot_cold_snacks', label: 'Hot and Cold Snacks/Fillers' },
  { value: 'meals_salads', label: 'Meals + Salads' },
  { value: 'multiseed_sandwiches', label: 'Multi-seed Low GI Sandwiches' },
  { value: 'ciabatta_sandwiches', label: 'Mini Ciabatta Sandwiches' },
  { value: 'wraps', label: 'Wraps' },
  { value: 'sweets', label: 'Sweets' }
];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-purple-100 text-purple-800',
  ready: 'bg-green-100 text-green-800',
  delivered: 'bg-teal-100 text-teal-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

const DEPOSIT_STATUS_COLORS = {
  pending: 'bg-red-100 text-red-700',
  partial: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700'
};

export default function AdminCateringPage({ authHeaders }) {
  const [activeTab, setActiveTab] = useState('stats'); // stats, menu, orders, calendar, clients
  const [loading, setLoading] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Menu state
  const [menuItems, setMenuItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null); // Full edit modal
  const [editForm, setEditForm] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '', category: 'sweets', description: '', price: 0, unit: 'each', 
    min_qty: 1, is_available: true, special_instructions: '', special_instructions_options: []
  });
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(null);
  const [depositForm, setDepositForm] = useState({ amount: '', method: 'cash', reference: '', notes: '' });
  
  // Edit Order Modal state
  const [showEditOrderModal, setShowEditOrderModal] = useState(null);
  const [editOrderForm, setEditOrderForm] = useState({
    items: [],
    event_date: '',
    event_time: '',
    guest_count: 0,
    occasion: '',
    delivery_address: '',
    needs_delivery: false,
    special_requests: '',
    manual_subtotal: null,
    manual_prep_handling_fee: null,
    manual_delivery_fee: null,
    manual_discount: 0,
    discount_reason: '',
    fully_paid: false,
    deposit_paid: false,
    deposit_status: 'pending'
  });
  const [editOrderLoading, setEditOrderLoading] = useState(false);
  const [cateringMenuItems, setCateringMenuItems] = useState([]);
  
  // Calendar state
  const [calendarData, setCalendarData] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDateOrders, setSelectedDateOrders] = useState(null);
  
  // Export state
  const [exportDateRange, setExportDateRange] = useState({ start: '', end: '', status: 'all' });
  const [exporting, setExporting] = useState(false);
  
  // Clients state
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  
  // Payment proofs state
  const [paymentProofs, setPaymentProofs] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState('pending_verification');
  const [selectedProof, setSelectedProof] = useState(null);
  const [proofImageData, setProofImageData] = useState(null);
  
  // Excel upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  
  // Category management state
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  
  // AI Image generation state
  const [generatingImageFor, setGeneratingImageFor] = useState(null);
  
  // Min qty fix state (one-time migration)
  const [fixingMinQty, setFixingMinQty] = useState(false);
  const [minQtyFixed, setMinQtyFixed] = useState(false);
  
  // Corporate credits state
  const [corporateCredits, setCorporateCredits] = useState([]);
  const [showCreditForm, setShowCreditForm] = useState(false);
  const [creditForm, setCreditForm] = useState({
    client_id: '', company_name: '', credit_limit: '', payment_terms: 'net_30',
    deposit_waiver: false, deposit_percentage: 30, reason: ''
  });

  useEffect(() => {
    if (activeTab === 'stats') fetchStats();
    else if (activeTab === 'menu') fetchMenu();
    else if (activeTab === 'orders') fetchOrders();
    else if (activeTab === 'calendar') fetchCalendar();
    else if (activeTab === 'clients') fetchClients();
    else if (activeTab === 'payments') { fetchPaymentProofs(); fetchCorporateCredits(); }
  }, [activeTab]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await api.get('/catering/admin/stats', { headers: authHeaders });
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const [menuRes, catRes] = await Promise.all([
        api.get('/catering/admin/menu', { headers: authHeaders }),
        api.get('/catering/admin/categories', { headers: authHeaders })
      ]);
      
      // FORCE min_qty=1 for Meals + Salads items by NAME (hardcoded fix)
      const mealsSaladsPatterns = ['salad', 'fish fingers', 'chicken nibbles', 'chicken goujon', 'samosa', 'spring roll', 'pumpkin', 'feta'];
      const shouldBeMinQty1 = (name) => {
        const lowerName = (name || '').toLowerCase();
        return mealsSaladsPatterns.some(p => lowerName.includes(p));
      };
      
      const items = (menuRes.data.items || []).map(item => 
        (item.category === 'meals_salads' || (item.category || '').toLowerCase().includes('meal') || (item.category || '').toLowerCase().includes('salad') || shouldBeMinQty1(item.name))
          ? { ...item, min_qty: 1, min_quantity: 1 }
          : item
      );
      setMenuItems(items);
      
      // Set categories from API
      const cats = catRes.data.categories || [];
      const formattedCats = cats.map(c => ({
        value: c.category_id,
        label: c.name
      }));
      setDynamicCategories(formattedCats);
      
    } catch (err) {
      toast.error('Failed to load menu');
    } finally {
      setLoading(false);
    }
  };

  // One-time fix for Meals + Salads min_qty (change from 5 to 1)
  const handleFixMealsSaladsMinQty = async () => {
    setFixingMinQty(true);
    try {
      const res = await api.post('/catering/admin/menu/fix-meals-salads-min-qty', {}, { headers: authHeaders });
      toast.success(`Fixed! ${res.data.modified} items updated to min qty = 1`);
      setMinQtyFixed(true);
      // Refresh menu to show updated values
      fetchMenu();
    } catch (err) {
      toast.error('Failed to fix min qty: ' + (err.response?.data?.detail || err.message));
    } finally {
      setFixingMinQty(false);
    }
  };

  // Excel file upload handler
  const handleExcelUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    
    setIsUploading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/catering/admin/menu/upload-excel', formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploadResult(res.data);
      toast.success(`Menu updated: ${res.data.summary.items_created} items in ${res.data.summary.categories_created} categories`);
      
      // Refresh the menu list
      await fetchMenu();
      
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to upload Excel file';
      toast.error(errorMsg);
      setUploadResult({ success: false, error: errorMsg });
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };
  
  // Download current menu as Excel template
  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/catering/admin/menu/export-template', {
        headers: authHeaders,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'catering_menu_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = orderFilter !== 'all' ? `?status=${orderFilter}` : '';
      const res = await api.get(`/catering/admin/orders${params}`, { headers: authHeaders });
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await api.get('/catering/admin/clients', { headers: authHeaders });
      setClients(res.data.clients || []);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch catering menu items for order editing
  const fetchCateringMenuItems = async () => {
    try {
      const res = await api.get('/catering/menu', { headers: authHeaders });
      setCateringMenuItems(res.data.items || []);
    } catch (err) {
      console.error('Failed to load catering menu for editing');
    }
  };
  
  // Open Edit Order Modal
  const openEditOrderModal = async (order) => {
    // Fetch menu items if not loaded
    if (cateringMenuItems.length === 0) {
      await fetchCateringMenuItems();
    }
    
    setEditOrderForm({
      items: (order.items || []).map(item => ({
        item_id: item.item_id || '',
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        unit: item.unit || 'each',
        special_instruction: item.special_instruction || ''
      })),
      event_date: order.event_date || '',
      event_time: order.event_time || '',
      guest_count: order.guest_count || 0,
      occasion: order.occasion || '',
      delivery_address: order.delivery_address || '',
      needs_delivery: order.needs_delivery || false,
      special_requests: order.special_requests || '',
      // Initialize manual overrides from stored values if they differ from auto-calculated
      // This preserves admin adjustments when reopening the modal
      manual_subtotal: order.subtotal || null,
      manual_prep_handling_fee: order.prep_handling_fee ?? order.packaging_fee ?? null,
      manual_delivery_fee: order.delivery_fee ?? null,
      manual_discount: order.discount || 0,
      discount_reason: order.discount_reason || '',
      fully_paid: order.fully_paid || false,
      deposit_paid: order.deposit_paid || order.deposit_status === 'paid',
      deposit_status: order.deposit_status || 'pending'
    });
    setShowEditOrderModal(order.order_id);
  };
  
  // Add item to edit order
  const addItemToEditOrder = (menuItem) => {
    const existingIndex = editOrderForm.items.findIndex(i => i.name === menuItem.name);
    if (existingIndex >= 0) {
      // Increase quantity
      const newItems = [...editOrderForm.items];
      newItems[existingIndex].quantity += 1;
      setEditOrderForm(prev => ({ ...prev, items: newItems }));
    } else {
      // Add new item
      setEditOrderForm(prev => ({
        ...prev,
        items: [...prev.items, {
          item_id: menuItem.item_id || '',
          name: menuItem.name,
          quantity: menuItem.min_qty || 1,
          unit_price: menuItem.price,
          unit: menuItem.unit || 'each',
          special_instruction: ''
        }]
      }));
    }
  };
  
  // Update item in edit order
  const updateEditOrderItem = (index, field, value) => {
    const newItems = [...editOrderForm.items];
    newItems[index][field] = value;
    setEditOrderForm(prev => ({ ...prev, items: newItems }));
  };
  
  // Remove item from edit order
  const removeEditOrderItem = (index) => {
    const newItems = editOrderForm.items.filter((_, i) => i !== index);
    setEditOrderForm(prev => ({ ...prev, items: newItems }));
  };
  
  // Calculate totals for edit order preview
  const calculateEditOrderTotals = () => {
    const subtotal = editOrderForm.manual_subtotal ?? editOrderForm.items.reduce(
      (sum, item) => sum + (item.quantity * item.unit_price), 0
    );
    const prepHandlingFee = editOrderForm.manual_prep_handling_fee ?? (subtotal * 0.30);
    const deliveryFee = editOrderForm.manual_delivery_fee ?? (editOrderForm.needs_delivery ? 10 : 0);
    const discount = editOrderForm.manual_discount || 0;
    const grossTotal = subtotal + prepHandlingFee + deliveryFee - discount;
    const depositRequired = grossTotal * 0.30;
    
    return { subtotal, prepHandlingFee, deliveryFee, discount, grossTotal, depositRequired };
  };
  
  // Save edited order
  const handleSaveEditedOrder = async () => {
    if (editOrderForm.items.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }
    
    setEditOrderLoading(true);
    try {
      const payload = {
        items: editOrderForm.items.map(item => ({
          item_id: item.item_id || null,
          name: item.name,
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          unit: item.unit || 'each',
          special_instruction: item.special_instruction || null
        })),
        event_date: editOrderForm.event_date || null,
        event_time: editOrderForm.event_time || null,
        guest_count: editOrderForm.guest_count ? parseInt(editOrderForm.guest_count) : null,
        occasion: editOrderForm.occasion || null,
        delivery_address: editOrderForm.delivery_address || null,
        needs_delivery: editOrderForm.needs_delivery,
        special_requests: editOrderForm.special_requests || null,
        manual_subtotal: editOrderForm.manual_subtotal,
        manual_prep_handling_fee: editOrderForm.manual_prep_handling_fee,
        manual_delivery_fee: editOrderForm.manual_delivery_fee,
        manual_discount: editOrderForm.manual_discount || null,
        discount_reason: editOrderForm.discount_reason || null,
        fully_paid: editOrderForm.fully_paid,
        deposit_paid: editOrderForm.deposit_paid,
        deposit_status: editOrderForm.deposit_status
      };
      
      const res = await api.put(`/catering/admin/orders/${showEditOrderModal}/edit`, payload, { headers: authHeaders });
      
      if (res.data.success) {
        toast.success(`Order updated! ${res.data.changes?.length || 0} change(s) made.`);
        setShowEditOrderModal(null);
        fetchOrders();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update order');
    } finally {
      setEditOrderLoading(false);
    }
  };
  
  // Payment proof functions
  const fetchPaymentProofs = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/catering/admin/payment-proofs?status=${paymentFilter}`, { headers: authHeaders });
      setPaymentProofs(res.data.proofs || []);
    } catch (err) {
      toast.error('Failed to load payment proofs');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProofImage = async (proofId) => {
    try {
      const res = await api.get(`/catering/admin/payment-proofs/${proofId}/image`, { headers: authHeaders });
      setProofImageData(res.data);
    } catch (err) {
      toast.error('Failed to load proof image');
    }
  };
  
  const verifyPaymentProof = async (proofId, verified, notes = '') => {
    try {
      await api.post(`/catering/admin/payment-proofs/${proofId}/verify`, 
        { verified, notes }, 
        { headers: authHeaders }
      );
      toast.success(verified ? 'Payment verified!' : 'Payment rejected');
      setSelectedProof(null);
      setProofImageData(null);
      fetchPaymentProofs();
    } catch (err) {
      toast.error('Failed to verify payment');
    }
  };
  
  // Corporate credits functions
  const fetchCorporateCredits = async () => {
    try {
      const res = await api.get('/catering/admin/corporate-credits', { headers: authHeaders });
      setCorporateCredits(res.data.credits || []);
    } catch (err) {
      console.error('Failed to load corporate credits');
    }
  };
  
  const saveCorporateCredit = async () => {
    if (!creditForm.client_id || !creditForm.company_name || !creditForm.credit_limit) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await api.post('/catering/admin/corporate-credits', 
        { ...creditForm, credit_limit: parseFloat(creditForm.credit_limit) },
        { headers: authHeaders }
      );
      toast.success('Credit terms saved');
      setShowCreditForm(false);
      setCreditForm({
        client_id: '', company_name: '', credit_limit: '', payment_terms: 'net_30',
        deposit_waiver: false, deposit_percentage: 30, reason: ''
      });
      fetchCorporateCredits();
    } catch (err) {
      toast.error('Failed to save credit terms');
    }
  };
  
  const revokeCorporateCredit = async (clientId) => {
    if (!confirm('Are you sure you want to revoke credit terms for this client?')) return;
    try {
      await api.delete(`/catering/admin/corporate-credits/${clientId}`, { headers: authHeaders });
      toast.success('Credit terms revoked');
      fetchCorporateCredits();
    } catch (err) {
      toast.error('Failed to revoke credit terms');
    }
  };

  // Calendar functions
  const fetchCalendar = async (monthDate = calendarMonth) => {
    setLoading(true);
    try {
      const startDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1).toISOString().split('T')[0];
      const endDate = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).toISOString().split('T')[0];
      const res = await api.get(`/catering/admin/calendar?start_date=${startDate}&end_date=${endDate}`, { headers: authHeaders });
      setCalendarData(res.data);
    } catch (err) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(calendarMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarMonth(newDate);
    fetchCalendar(newDate);
    setSelectedDateOrders(null);
  };

  // Export functions
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format });
      if (exportDateRange.start) params.append('start_date', exportDateRange.start);
      if (exportDateRange.end) params.append('end_date', exportDateRange.end);
      if (exportDateRange.status && exportDateRange.status !== 'all') params.append('status', exportDateRange.status);
      
      const res = await api.get(`/catering/admin/orders/export?${params.toString()}`, { 
        headers: authHeaders,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `catering_orders_${exportDateRange.start || 'all'}_${exportDateRange.end || 'all'}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Orders exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Failed to export orders');
    } finally {
      setExporting(false);
    }
  };

  // Deposit functions
  const handleRecordDeposit = async (orderId) => {
    if (!depositForm.amount || parseFloat(depositForm.amount) <= 0) {
      toast.error('Please enter a valid deposit amount');
      return;
    }
    setLoading(true);
    try {
      const res = await api.put(`/catering/admin/orders/${orderId}/deposit`, {
        deposit_amount: parseFloat(depositForm.amount),
        payment_method: depositForm.method,
        payment_reference: depositForm.reference || null,
        notes: depositForm.notes || null
      }, { headers: authHeaders });
      
      if (res.data.success) {
        toast.success(res.data.message);
        setShowDepositModal(null);
        setDepositForm({ amount: '', method: 'cash', reference: '', notes: '' });
        fetchOrders();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record deposit');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedMenu = async () => {
    if (!confirm('This will populate the catering menu with default items. Continue?')) return;
    setLoading(true);
    try {
      const res = await api.post('/catering/admin/menu/seed', {}, { headers: authHeaders });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchMenu();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to seed menu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/catering/admin/menu', newItem, { headers: authHeaders });
      if (res.data.success) {
        toast.success('Item added');
        setShowAddForm(false);
        setNewItem({ name: '', category: 'sweets', description: '', price: 0, unit: 'each', min_qty: 1, is_available: true });
        fetchMenu();
      }
    } catch (err) {
      toast.error('Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      const res = await api.put(`/catering/admin/menu/${itemId}`, updates, { headers: authHeaders });
      if (res.data.success) {
        toast.success('Item updated');
        setEditingItem(null);
        fetchMenu();
      }
    } catch (err) {
      toast.error('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm('Delete this item?')) return;
    try {
      const res = await api.delete(`/catering/admin/menu/${itemId}`, { headers: authHeaders });
      if (res.data.success) {
        toast.success('Item deleted');
        fetchMenu();
      }
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };
  
  // Open edit modal with item data
  const openEditModal = (item) => {
    setEditForm({
      ...item,
      special_instructions_options: item.special_instructions_options || []
    });
    setShowEditModal(item.item_id);
  };
  
  // Save full item edit
  const handleSaveFullEdit = async () => {
    setLoading(true);
    try {
      const res = await api.put(`/catering/admin/menu/${editForm.item_id}`, {
        name: editForm.name,
        category: editForm.category,
        description: editForm.description || '',
        price: parseFloat(editForm.price) || 0,
        unit: editForm.unit || 'each',
        min_qty: parseInt(editForm.min_qty) || 1,
        is_available: editForm.is_available,
        special_instructions: editForm.special_instructions || '',
        special_instructions_options: editForm.special_instructions_options || [],
        dietary_tags: editForm.dietary_tags || []
      }, { headers: authHeaders });
      
      if (res.data.success) {
        toast.success('Item updated successfully');
        setShowEditModal(null);
        setEditForm({});
        fetchMenu();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update item');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle item availability
  const toggleItemAvailability = async (item) => {
    try {
      const res = await api.put(`/catering/admin/menu/${item.item_id}`, {
        is_available: !item.is_available
      }, { headers: authHeaders });
      
      if (res.data.success) {
        toast.success(item.is_available ? 'Item hidden from menu' : 'Item now visible on menu');
        fetchMenu();
      }
    } catch (err) {
      toast.error('Failed to update availability');
    }
  };
  
  // Upload image for item
  const handleImageUpload = async (itemId, file) => {
    if (!file) return;
    
    // Validate file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images allowed');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post(`/catering/admin/menu/${itemId}/image`, formData, {
        headers: { 
          ...authHeaders,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        toast.success('Image uploaded successfully');
        fetchMenu();
        // Update edit form if modal is open
        if (showEditModal === itemId) {
          const updatedItem = await api.get('/catering/admin/menu', { headers: authHeaders });
          const item = updatedItem.data.items?.find(i => i.item_id === itemId);
          if (item) {
            setEditForm(prev => ({ ...prev, image: item.image }));
          }
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };
  
  // Generate AI image for item
  const handleGenerateAIImage = async (itemId) => {
    setGeneratingImageFor(itemId);
    try {
      const res = await api.post(`/catering/admin/menu/${itemId}/generate-ai-image`, {}, {
        headers: authHeaders
      });
      
      if (res.data.success) {
        toast.success('AI image generated successfully');
        fetchMenu();
        // Update edit form if modal is open
        if (showEditModal === itemId) {
          setEditForm(prev => ({ ...prev, image: res.data.url }));
        }
      } else {
        toast.error(res.data.error || 'Failed to generate image');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate AI image');
    } finally {
      setGeneratingImageFor(null);
    }
  };
  
  // Category management functions
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      const res = await api.post('/catering/admin/categories', newCategory, { headers: authHeaders });
      if (res.data.success) {
        toast.success('Category created');
        setNewCategory({ name: '', description: '' });
        setShowAddCategory(false);
        fetchMenu();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    }
  };
  
  const handleUpdateCategory = async (categoryId, updates) => {
    try {
      await api.put(`/catering/admin/categories/${categoryId}`, updates, { headers: authHeaders });
      toast.success('Category updated');
      setEditingCategory(null);
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update category');
    }
  };
  
  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      await api.delete(`/catering/admin/categories/${categoryId}`, { headers: authHeaders });
      toast.success('Category deleted');
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };
  
  // Add special instruction option
  const addInstructionOption = () => {
    setEditForm(prev => ({
      ...prev,
      special_instructions_options: [...(prev.special_instructions_options || []), '']
    }));
  };
  
  // Update special instruction option
  const updateInstructionOption = (index, value) => {
    setEditForm(prev => {
      const options = [...(prev.special_instructions_options || [])];
      options[index] = value;
      return { ...prev, special_instructions_options: options };
    });
  };
  
  // Remove special instruction option
  const removeInstructionOption = (index) => {
    setEditForm(prev => {
      const options = [...(prev.special_instructions_options || [])];
      options.splice(index, 1);
      return { ...prev, special_instructions_options: options };
    });
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const res = await api.put(`/catering/admin/orders/${orderId}/status?status=${status}`, {}, { headers: authHeaders });
      if (res.data.success) {
        toast.success(`Order status updated to ${status}`);
        fetchOrders();
      }
    } catch (err) {
      toast.error('Failed to update order');
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Group menu items by category
  const groupedItems = menuItems.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const filteredClients = clients.filter(c => 
    !clientSearch || 
    c.email?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.full_name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Catering Management</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage catering menu, orders, and corporate clients</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {[
          { id: 'stats', label: 'Dashboard', icon: BarChart3 },
          { id: 'calendar', label: 'Calendar', icon: Calendar },
          { id: 'menu', label: 'Menu Items', icon: Package },
          { id: 'orders', label: 'Orders', icon: FileText },
          { id: 'payments', label: 'Payments', icon: CreditCard },
          { id: 'clients', label: 'Clients', icon: Users }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-amber-600 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
            data-testid={`catering-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Stats/Dashboard Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : stats ? (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Orders</p>
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.overview.total_orders}</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                      <FileText className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-sm text-green-600 mt-2">{stats.overview.completed_orders} completed</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-600">${stats.revenue.all_time.toLocaleString()}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Last 30 days: ${stats.revenue.last_30_days.toLocaleString()}</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Pending Orders</p>
                      <p className="text-3xl font-bold text-amber-600">{stats.overview.pending_orders}</p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                  <p className="text-sm text-amber-600 mt-2">${stats.revenue.pending.toLocaleString()} pending</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Clients</p>
                      <p className="text-3xl font-bold text-indigo-600">{stats.overview.total_clients}</p>
                    </div>
                    <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-indigo-600" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Avg order: ${stats.overview.avg_order_value}</p>
                </div>
              </div>
              
              {/* Popular Items & Status Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Popular Items */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    Popular Items
                  </h3>
                  <div className="space-y-3">
                    {stats.popular_items.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            idx === 0 ? 'bg-amber-500 text-white' : 
                            idx === 1 ? 'bg-gray-400 text-white' : 
                            idx === 2 ? 'bg-amber-700 text-white' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="text-gray-900 dark:text-white">{item.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-amber-600">{item.quantity}</span>
                          <span className="text-gray-500 text-sm ml-1">units</span>
                        </div>
                      </div>
                    ))}
                    {stats.popular_items.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No order data yet</p>
                    )}
                  </div>
                </div>
                
                {/* Status Breakdown */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-amber-600" />
                    Orders by Status
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(stats.status_breakdown).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[status]}`}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                        <div className="flex-1 mx-4">
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                status === 'completed' ? 'bg-gray-500' :
                                status === 'pending' ? 'bg-yellow-500' :
                                status === 'confirmed' ? 'bg-blue-500' :
                                status === 'cancelled' ? 'bg-red-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${stats.overview.total_orders > 0 ? (count / stats.overview.total_orders) * 100 : 0}%` }}
                            />
                          </div>
                        </div>
                        <span className="font-semibold text-gray-900 dark:text-white w-8 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Recent Orders & Occasions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Orders */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-600" />
                    Recent Orders
                  </h3>
                  <div className="space-y-3">
                    {stats.recent_orders.map((order, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{order.company_name || order.client_name}</p>
                          <p className="text-sm text-gray-500">{order.event_date}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-amber-600">${order.total?.toFixed(2)}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[order.status]}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                    {stats.recent_orders.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No orders yet</p>
                    )}
                  </div>
                </div>
                
                {/* Orders by Occasion */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-amber-600" />
                    Orders by Occasion
                  </h3>
                  <div className="space-y-3">
                    {stats.occasions.slice(0, 6).map((occ, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="text-gray-900 dark:text-white capitalize">{occ.occasion}</span>
                        <span className="font-semibold text-amber-600">{occ.count} orders</span>
                      </div>
                    ))}
                    {stats.occasions.length === 0 && (
                      <p className="text-gray-500 text-center py-4">No order data yet</p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-center py-12">Failed to load statistics</p>
          )}
        </div>
      )}

      {/* Menu Tab */}
      {activeTab === 'menu' && (
        <div className="space-y-4">
          {/* Excel Upload Section */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bulk Menu Update
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Upload an Excel file to replace all catering menu items and categories at once
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300"
                  data-testid="catering-download-template-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Current Menu
                </Button>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                    className="hidden"
                    disabled={isUploading}
                    data-testid="catering-excel-upload-input"
                  />
                  <Button
                    asChild
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    disabled={isUploading}
                  >
                    <span>
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Excel File
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
            
            {/* Upload Result */}
            {uploadResult && (
              <div className={`mt-4 p-3 rounded-lg ${uploadResult.success 
                ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700' 
                : 'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700'}`}>
                {uploadResult.success ? (
                  <div>
                    <div className="flex items-center gap-2 text-green-800 dark:text-green-200 font-medium">
                      <Check className="w-5 h-5" />
                      Menu Updated Successfully
                    </div>
                    <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                      <p><strong>Categories:</strong> {uploadResult.summary?.categories_created} ({uploadResult.summary?.categories?.join(', ')})</p>
                      <p><strong>Items:</strong> {uploadResult.summary?.items_created} total</p>
                      {uploadResult.summary?.items_by_category && (
                        <ul className="mt-1 ml-4 list-disc">
                          {Object.entries(uploadResult.summary.items_by_category).map(([cat, count]) => (
                            <li key={cat}>{cat}: {count} items</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
                    <AlertCircle className="w-5 h-5" />
                    {uploadResult.error || 'Upload failed'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category Management Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Package className="w-5 h-5" />
                Categories ({dynamicCategories.length})
              </h3>
              <Button
                onClick={() => setShowAddCategory(true)}
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Category
              </Button>
            </div>
            
            {/* Add Category Form */}
            {showAddCategory && (
              <form onSubmit={handleAddCategory} className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    placeholder="Category name *"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                  <Input
                    placeholder="Description (optional)"
                    value={newCategory.description}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                      <Check className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowAddCategory(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            )}
            
            {/* Category List */}
            {dynamicCategories.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No categories yet. Add your first category above.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {dynamicCategories.map(cat => (
                  <div 
                    key={cat.value} 
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg"
                  >
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{cat.label}</span>
                    <button
                      onClick={() => handleDeleteCategory(cat.value)}
                      className="text-red-500 hover:text-red-700"
                      title="Delete category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="catering-add-item-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
            {menuItems.length === 0 && (
              <Button
                onClick={handleSeedMenu}
                variant="outline"
                data-testid="catering-seed-menu-btn"
              >
                <Package className="w-4 h-4 mr-2" />
                Seed Default Menu
              </Button>
            )}
          </div>

          {/* Add Item Form */}
          {showAddForm && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-amber-200 dark:border-amber-800">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">Add New Item</h3>
                <button onClick={() => setShowAddForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddItem} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                  <Input
                    value={newItem.name}
                    onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                    required
                    data-testid="catering-item-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                    data-testid="catering-item-category"
                  >
                    {dynamicCategories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Price ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newItem.price}
                    onChange={(e) => setNewItem(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    data-testid="catering-item-price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unit</label>
                  <Input
                    value={newItem.unit}
                    onChange={(e) => setNewItem(prev => ({ ...prev, unit: e.target.value }))}
                    placeholder="each, per 10, per kilo"
                    data-testid="catering-item-unit"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min Qty</label>
                  <Input
                    type="number"
                    value={newItem.min_qty}
                    onChange={(e) => setNewItem(prev => ({ ...prev, min_qty: parseInt(e.target.value) || 1 }))}
                    data-testid="catering-item-minqty"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                  <Input
                    value={newItem.description}
                    onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Optional description"
                    data-testid="catering-item-description"
                  />
                </div>
                <div className="flex items-end">
                  <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="catering-item-save">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Item
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Menu Items by Category */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : (
            <div className="space-y-4">
              {dynamicCategories.map(cat => {
                const items = groupedItems[cat.value] || [];
                if (items.length === 0) return null;
                const isExpanded = expandedCategories[cat.value] !== false;
                
                return (
                  <div key={cat.value} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                    <button
                      onClick={() => toggleCategory(cat.value)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                    >
                      <span className="font-semibold text-amber-900 dark:text-amber-100">{cat.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-amber-700 dark:text-amber-300">{items.length} items</span>
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {items.map(item => (
                          <div key={item.item_id} className={`p-4 flex items-center gap-4 ${!item.is_available ? 'bg-gray-50 dark:bg-gray-900/50 opacity-70' : ''}`}>
                            {/* Item Image Thumbnail */}
                            <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                              {item.image ? (
                                <img 
                                  src={item.image} 
                                  alt={item.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <ImageIcon className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            
                            {/* Item Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleItemAvailability(item)}
                                  className={`p-1 rounded-full transition-colors ${
                                    item.is_available 
                                      ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                      : 'bg-red-100 text-red-600 hover:bg-red-200'
                                  }`}
                                  title={item.is_available ? 'Click to hide from menu' : 'Click to show on menu'}
                                >
                                  {item.is_available ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                                <span className="font-medium text-gray-900 dark:text-white truncate">{item.name}</span>
                                {item.special_instructions_options?.length > 0 && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                    Options
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <p className="text-sm text-gray-500 mt-0.5 truncate">{item.description}</p>
                              )}
                              {item.special_instructions && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 truncate">
                                  Instructions: {item.special_instructions}
                                </p>
                              )}
                            </div>
                            
                            {/* Unit & Min Qty */}
                            <div className="hidden sm:block text-sm text-gray-500 w-24">{item.unit}</div>
                            <div className="hidden sm:block text-sm text-gray-500 w-16">Min: {item.min_qty}</div>
                            
                            {/* Price */}
                            <div className="font-semibold text-amber-600 w-20 text-right">
                              {item.price > 0 ? `$${item.price.toFixed(2)}` : 'POA'}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleGenerateAIImage(item.item_id)}
                                disabled={generatingImageFor === item.item_id}
                                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                title="Generate AI Image"
                              >
                                {generatingImageFor === item.item_id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <ImageIcon className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditModal(item)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-item-${item.item_id}`}
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteItem(item.item_id)}
                                data-testid={`delete-item-${item.item_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Full Edit Modal */}
          {showEditModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={() => setShowEditModal(null)}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Menu Item</h2>
                  <button 
                    onClick={() => setShowEditModal(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Image Upload Section */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-6">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                      {/* Current Image */}
                      <div className="relative w-32 h-32 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                        {editForm.image ? (
                          <img 
                            src={editForm.image} 
                            alt={editForm.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                            <ImageIcon className="w-10 h-10 mb-2" />
                            <span className="text-xs">No image</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Upload Controls */}
                      <div className="flex-1 text-center sm:text-left">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Item Image</h4>
                        <p className="text-sm text-gray-500 mb-3">Upload an image or generate with AI</p>
                        <div className="flex flex-wrap gap-2">
                          <label className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg cursor-pointer transition-colors">
                            {uploadingImage ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Upload Image
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={(e) => handleImageUpload(editForm.item_id, e.target.files[0])}
                              disabled={uploadingImage}
                            />
                          </label>
                          <Button
                            type="button"
                            onClick={() => handleGenerateAIImage(editForm.item_id)}
                            disabled={generatingImageFor === editForm.item_id}
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            {generatingImageFor === editForm.item_id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <ImageIcon className="w-4 h-4 mr-2" />
                                AI Generate
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Item Name *
                      </label>
                      <Input
                        value={editForm.name || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="edit-item-name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <select
                        value={editForm.category || 'sweets'}
                        onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      >
                        {dynamicCategories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Price ($)
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editForm.price || 0}
                        onChange={(e) => setEditForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Unit
                      </label>
                      <Input
                        value={editForm.unit || 'each'}
                        onChange={(e) => setEditForm(prev => ({ ...prev, unit: e.target.value }))}
                        placeholder="each, per 10, per kilo"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Minimum Quantity
                      </label>
                      <Input
                        type="number"
                        value={editForm.min_qty || 1}
                        onChange={(e) => setEditForm(prev => ({ ...prev, min_qty: parseInt(e.target.value) || 1 }))}
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <Textarea
                        value={editForm.description || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Item description (optional)"
                        rows={2}
                      />
                    </div>
                  </div>
                  
                  {/* Availability Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">Available for Online Ordering</h4>
                      <p className="text-sm text-gray-500">When off, this item will not appear on the catering menu</p>
                    </div>
                    <button
                      onClick={() => setEditForm(prev => ({ ...prev, is_available: !prev.is_available }))}
                      className={`relative w-14 h-8 rounded-full transition-colors ${
                        editForm.is_available ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      data-testid="edit-item-availability"
                    >
                      <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                        editForm.is_available ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>
                  
                  {/* Special Instructions Section */}
                  <div className="border border-blue-200 dark:border-blue-800 rounded-xl p-4 bg-blue-50/50 dark:bg-blue-900/20">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <Settings className="w-4 h-4 text-blue-600" />
                      Special Instructions
                    </h4>
                    <p className="text-sm text-gray-500 mb-4">
                      Add instructions or options that appear when customers order this item (e.g., Small or Large, Glaze preference)
                    </p>
                    
                    {/* Instruction Label */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Instruction Label
                      </label>
                      <Input
                        value={editForm.special_instructions || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, special_instructions: e.target.value }))}
                        placeholder="e.g., Select size, Choose glaze, Dietary requirements"
                      />
                    </div>
                    
                    {/* Instruction Options */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Options (dropdown choices for customer)
                      </label>
                      <div className="space-y-2">
                        {(editForm.special_instructions_options || []).map((option, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              value={option}
                              onChange={(e) => updateInstructionOption(idx, e.target.value)}
                              placeholder={`Option ${idx + 1} (e.g., Small, Large)`}
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeInstructionOption(idx)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addInstructionOption}
                          className="w-full mt-2"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Option
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveFullEdit}
                    disabled={loading}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="save-edit-item"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {/* Export Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Download className="w-5 h-5 text-amber-600" />
              Export Orders
            </h3>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <Input
                  type="date"
                  value={exportDateRange.start}
                  onChange={(e) => setExportDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-40"
                  data-testid="export-start-date"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <Input
                  type="date"
                  value={exportDateRange.end}
                  onChange={(e) => setExportDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-40"
                  data-testid="export-end-date"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Status</label>
                <select
                  value={exportDateRange.status}
                  onChange={(e) => setExportDateRange(prev => ({ ...prev, status: e.target.value }))}
                  className="h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm"
                  data-testid="export-status"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <Button
                onClick={() => handleExport('csv')}
                disabled={exporting}
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
                data-testid="export-csv-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                CSV
              </Button>
              <Button
                onClick={() => handleExport('excel')}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-excel-btn"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Excel
              </Button>
            </div>
          </div>

          {/* Filter */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'confirmed', 'preparing', 'ready', 'delivered', 'completed', 'cancelled'].map(status => (
              <Button
                key={status}
                size="sm"
                variant={orderFilter === status ? 'default' : 'outline'}
                onClick={() => { setOrderFilter(status); fetchOrders(); }}
                className={orderFilter === status ? 'bg-amber-600 text-white' : ''}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>

          {/* Orders List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No orders found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div key={order.order_id} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-bold text-gray-900 dark:text-white">#{order.order_id}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100'}`}>
                            {order.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${DEPOSIT_STATUS_COLORS[order.deposit_status] || DEPOSIT_STATUS_COLORS.pending}`}>
                            Deposit: {order.deposit_status || 'pending'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {order.company_name} • {order.client_name} • {new Date(order.event_date).toLocaleDateString()} at {order.event_time}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-amber-600">${order.total?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{order.guest_count} guests</p>
                        </div>
                        {/* Quick Delete Button - visible without expanding */}
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`⚠️ DELETE this order?\n\nOrder #${order.order_id}\n${order.client_name}\n$${order.total?.toFixed(2)}\n\nThis is permanent and cannot be undone.`)) {
                              try {
                                const res = await api.delete(`/catering/admin/orders/${order.order_id}`, { headers: authHeaders });
                                if (res.data.success) {
                                  toast.success('Order deleted');
                                  fetchOrders();
                                }
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to delete');
                              }
                            }
                          }}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete Order"
                          data-testid={`quick-delete-order-${order.order_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedOrder === order.order_id && (
                    <div className="border-t border-gray-100 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Event Details</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong>Occasion:</strong> {order.occasion} {order.occasion_other && `(${order.occasion_other})`}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <strong>Delivery:</strong> {order.delivery_address}
                          </p>
                          {order.delivery_instructions && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Instructions:</strong> {order.delivery_instructions}
                            </p>
                          )}
                          {order.special_requests && (
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Special Requests:</strong> {order.special_requests}
                            </p>
                          )}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Contact</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{order.client_email}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{order.client_phone}</p>
                        </div>
                      </div>
                      
                      {/* Items */}
                      <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Items ({order.items?.length})</h4>
                      <div className="space-y-1 mb-4">
                        {order.items?.map((item, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">{item.quantity}x {item.name}</span>
                            <span className="text-gray-900 dark:text-white">${(item.quantity * item.unit_price).toFixed(2)}</span>
                          </div>
                        ))}
                        <div className="border-t pt-2 mt-2 flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal</span>
                          <span>${order.subtotal?.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Prep & Handling (30%)</span>
                          <span>${(order.prep_handling_fee ?? order.packaging_fee ?? 0).toFixed(2)}</span>
                        </div>
                        {(order.delivery_fee > 0 || order.needs_delivery) && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Delivery</span>
                            <span>${(order.delivery_fee ?? 0).toFixed(2)}</span>
                          </div>
                        )}
                        {order.discount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Discount</span>
                            <span className="text-red-600">-${order.discount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold">
                          <span>Total</span>
                          <span className="text-amber-600">${(order.gross_total ?? order.total)?.toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* Deposit Tracking Section */}
                      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-4">
                        <h4 className="font-medium text-amber-900 dark:text-amber-100 mb-3 flex items-center gap-2">
                          <DollarSign className="w-5 h-5" />
                          Deposit Tracking (30%)
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Required</p>
                            <p className="font-bold text-lg text-amber-700 dark:text-amber-300">${order.deposit_required?.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Paid</p>
                            <p className="font-bold text-lg text-green-600">${(order.deposit_amount_paid || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Remaining</p>
                            <p className="font-bold text-lg text-red-600">
                              ${Math.max(0, (order.deposit_required || 0) - (order.deposit_amount_paid || 0)).toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600 dark:text-gray-400">Status</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${DEPOSIT_STATUS_COLORS[order.deposit_status] || DEPOSIT_STATUS_COLORS.pending}`}>
                              {(order.deposit_status || 'pending').toUpperCase()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Deposit Payment History */}
                        {order.deposit_payments && order.deposit_payments.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
                            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Payment History:</p>
                            {order.deposit_payments.map((payment, idx) => (
                              <div key={idx} className="text-xs text-gray-500 flex justify-between py-1">
                                <span>{new Date(payment.recorded_at).toLocaleDateString()} - {payment.payment_method}</span>
                                <span className="font-medium text-green-600">+${payment.amount.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Record Payment Button */}
                        {order.deposit_status !== 'paid' && !order.fully_paid && (
                          <Button
                            onClick={(e) => { e.stopPropagation(); setShowDepositModal(order.order_id); }}
                            className="mt-3 bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                            data-testid={`record-deposit-${order.order_id}`}
                          >
                            <Banknote className="w-4 h-4 mr-2" />
                            Record Payment
                          </Button>
                        )}
                        {order.fully_paid && (
                          <div className="mt-3 flex items-center gap-2 text-green-600">
                            <Check className="w-5 h-5" />
                            <span className="font-medium">Fully Paid</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Status Actions */}
                      <div className="flex gap-2 flex-wrap">
                        <span className="text-sm text-gray-500 mr-2">Update Status:</span>
                        {['confirmed', 'preparing', 'ready', 'delivered', 'completed', 'cancelled'].map(status => (
                          <Button
                            key={status}
                            size="sm"
                            variant="outline"
                            disabled={order.status === status}
                            onClick={() => handleUpdateOrderStatus(order.order_id, status)}
                            className={order.status === status ? 'opacity-50' : ''}
                          >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </Button>
                        ))}
                      </div>
                      
                      {/* Additional Actions */}
                      <div className="flex gap-2 flex-wrap mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const response = await api.get(`/catering/admin/orders/${order.order_id}/invoice`, {
                                headers: authHeaders,
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `Invoice-${order.order_id}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              toast.success('Invoice downloaded');
                            } catch (err) {
                              toast.error('Failed to download invoice');
                            }
                          }}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download Invoice
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const res = await api.post(`/catering/admin/orders/${order.order_id}/send-invoice`, {}, { headers: authHeaders });
                              toast.success(res.data.message);
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to send invoice');
                            }
                          }}
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Email Invoice
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              const res = await api.post(`/catering/admin/calendar/sync-order/${order.order_id}`, {}, { headers: authHeaders });
                              if (res.data.success) {
                                toast.success('Synced to Google Calendar!');
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Calendar sync failed');
                            }
                          }}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Sync to Calendar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800"
                          onClick={() => openEditOrderModal(order)}
                          data-testid={`edit-order-${order.order_id}`}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit Order
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 border-red-200 dark:border-red-800"
                          onClick={async () => {
                            if (window.confirm(`⚠️ PERMANENTLY DELETE this order?\n\nOrder ID: ${order.order_id}\nClient: ${order.client_name}\nTotal: $${order.total?.toFixed(2)}\n\nThis cannot be undone and will not affect metrics.`)) {
                              try {
                                const res = await api.delete(`/catering/admin/orders/${order.order_id}`, { headers: authHeaders });
                                if (res.data.success) {
                                  toast.success('Order permanently deleted');
                                  fetchOrders();
                                }
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to delete order');
                              }
                            }
                          }}
                          data-testid={`delete-order-${order.order_id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete Order
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Deposit Payment Modal */}
          {showDepositModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg text-gray-900 dark:text-white flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-green-600" />
                    Record Deposit Payment
                  </h3>
                  <button onClick={() => setShowDepositModal(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount ($) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={depositForm.amount}
                      onChange={(e) => setDepositForm(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="Enter payment amount"
                      data-testid="deposit-amount"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Method</label>
                    <select
                      value={depositForm.method}
                      onChange={(e) => setDepositForm(prev => ({ ...prev, method: e.target.value }))}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                      data-testid="deposit-method"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="card">Card</option>
                      <option value="mpaisa">M-PAiSA</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reference Number</label>
                    <Input
                      value={depositForm.reference}
                      onChange={(e) => setDepositForm(prev => ({ ...prev, reference: e.target.value }))}
                      placeholder="Transaction/receipt reference"
                      data-testid="deposit-reference"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                    <Textarea
                      value={depositForm.notes}
                      onChange={(e) => setDepositForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Optional notes"
                      rows={2}
                      data-testid="deposit-notes"
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={() => setShowDepositModal(null)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleRecordDeposit(showDepositModal)}
                      disabled={loading || !depositForm.amount}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid="confirm-deposit-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Record Payment'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Edit Order Modal */}
          {showEditOrderModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowEditOrderModal(null)}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="sticky top-0 bg-amber-50 dark:bg-amber-900/30 px-6 py-4 border-b border-amber-200 dark:border-amber-800 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-amber-900 dark:text-amber-100 flex items-center gap-2">
                    <Pencil className="w-5 h-5" />
                    Edit Order #{showEditOrderModal}
                  </h2>
                  <button 
                    onClick={() => setShowEditOrderModal(null)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {/* Order Items Section */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Package className="w-5 h-5 text-amber-600" />
                      Order Items ({editOrderForm.items.length})
                    </h3>
                    
                    {/* Current Items */}
                    <div className="space-y-2 mb-4">
                      {editOrderForm.items.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No items in order. Add items from the menu below.</p>
                      ) : (
                        editOrderForm.items.map((item, index) => (
                          <div key={index} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 dark:text-white truncate">{item.name}</p>
                              <p className="text-sm text-gray-500">{item.unit}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => updateEditOrderItem(index, 'quantity', Math.max(1, item.quantity - 1))}
                                className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600"
                              >
                                -
                              </button>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateEditOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                className="w-16 text-center"
                              />
                              <button
                                onClick={() => updateEditOrderItem(index, 'quantity', item.quantity + 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600"
                              >
                                +
                              </button>
                            </div>
                            <div className="w-24">
                              <Input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateEditOrderItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="text-right"
                                placeholder="Price"
                              />
                            </div>
                            <p className="w-20 text-right font-semibold text-amber-600">
                              ${(item.quantity * item.unit_price).toFixed(2)}
                            </p>
                            <button
                              onClick={() => removeEditOrderItem(index)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Add Items from Menu */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Add Items from Menu:</p>
                      <div className="max-h-48 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        {cateringMenuItems.length === 0 ? (
                          <p className="text-gray-500 text-center py-4">Loading menu items...</p>
                        ) : (
                          <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {cateringMenuItems.slice(0, 50).map((menuItem) => (
                              <div
                                key={menuItem.item_id}
                                className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                onClick={() => addItemToEditOrder(menuItem)}
                              >
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">{menuItem.name}</p>
                                  <p className="text-xs text-gray-500">{menuItem.unit} • Min: {menuItem.min_qty || 1}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-amber-600">${menuItem.price?.toFixed(2)}</span>
                                  <Plus className="w-4 h-4 text-green-600" />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Pricing Adjustments */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                      Price Adjustments (Optional Overrides)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Manual Subtotal ($)
                        </label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editOrderForm.manual_subtotal ?? ''}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, manual_subtotal: e.target.value ? parseFloat(e.target.value) : null }))}
                          placeholder="Auto-calculated"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Manual Prep & Handling ($)
                        </label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editOrderForm.manual_prep_handling_fee ?? ''}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, manual_prep_handling_fee: e.target.value ? parseFloat(e.target.value) : null }))}
                          placeholder="30% default"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Manual Delivery Fee ($)
                        </label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editOrderForm.manual_delivery_fee ?? ''}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, manual_delivery_fee: e.target.value ? parseFloat(e.target.value) : null }))}
                          placeholder="$10 if delivery"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Discount ($)
                        </label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editOrderForm.manual_discount || ''}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, manual_discount: parseFloat(e.target.value) || 0 }))}
                          placeholder="0"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Discount Reason
                        </label>
                        <Input
                          value={editOrderForm.discount_reason || ''}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, discount_reason: e.target.value }))}
                          placeholder="e.g., Repeat customer discount"
                        />
                      </div>
                    </div>
                    
                    {/* Calculated Totals Preview */}
                    {(() => {
                      const totals = calculateEditOrderTotals();
                      return (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                              <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Prep & Handling (30%):</span>
                              <span className="font-medium">${totals.prepHandlingFee.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 dark:text-gray-400">Delivery:</span>
                              <span className="font-medium">${totals.deliveryFee.toFixed(2)}</span>
                            </div>
                            {totals.discount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600 dark:text-gray-400">Discount:</span>
                                <span className="font-medium text-red-600">-${totals.discount.toFixed(2)}</span>
                              </div>
                            )}
                            <div className="flex justify-between col-span-2 md:col-span-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                              <span className="font-bold text-lg text-gray-900 dark:text-white">Grand Total:</span>
                              <span className="font-bold text-lg text-amber-600">${totals.grossTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between col-span-2 md:col-span-3">
                              <span className="text-gray-600 dark:text-gray-400">Deposit Required (30%):</span>
                              <span className="font-semibold text-amber-700">${totals.depositRequired.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Payment Status */}
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
                    <h3 className="font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Payment Status
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Deposit Status
                        </label>
                        <select
                          value={editOrderForm.deposit_status}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, deposit_status: e.target.value }))}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="partial">Partial</option>
                          <option value="paid">Paid</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editOrderForm.deposit_paid}
                            onChange={(e) => setEditOrderForm(prev => ({ ...prev, deposit_paid: e.target.checked }))}
                            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Deposit Paid</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editOrderForm.fully_paid}
                            onChange={(e) => setEditOrderForm(prev => ({ 
                              ...prev, 
                              fully_paid: e.target.checked,
                              deposit_paid: e.target.checked ? true : prev.deposit_paid,
                              deposit_status: e.target.checked ? 'paid' : prev.deposit_status
                            }))}
                            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">FULLY PAID</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Event Details */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-amber-600" />
                      Event Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Date</label>
                        <Input
                          type="date"
                          value={editOrderForm.event_date}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, event_date: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event Time</label>
                        <Input
                          type="time"
                          value={editOrderForm.event_time}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, event_time: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Guest Count</label>
                        <Input
                          type="number"
                          value={editOrderForm.guest_count}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, guest_count: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Occasion</label>
                        <Input
                          value={editOrderForm.occasion}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, occasion: e.target.value }))}
                          placeholder="e.g., Corporate Meeting"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Address</label>
                        <Input
                          value={editOrderForm.delivery_address}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                          placeholder="Delivery address"
                        />
                      </div>
                      <div className="flex items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editOrderForm.needs_delivery}
                            onChange={(e) => setEditOrderForm(prev => ({ ...prev, needs_delivery: e.target.checked }))}
                            className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Needs Delivery (+$10)</span>
                        </label>
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Special Requests</label>
                        <Textarea
                          value={editOrderForm.special_requests}
                          onChange={(e) => setEditOrderForm(prev => ({ ...prev, special_requests: e.target.value }))}
                          placeholder="Any special requests or notes"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Modal Footer */}
                <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditOrderModal(null)}
                    disabled={editOrderLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEditedOrder}
                    disabled={editOrderLoading || editOrderForm.items.length === 0}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="save-edit-order-btn"
                  >
                    {editOrderLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-6">
          {/* Calendar Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Calendar className="w-7 h-7 text-amber-600" />
              Catering Calendar
            </h2>
            <div className="flex items-center gap-2">
              <Button onClick={() => navigateMonth(-1)} variant="outline" size="sm">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="font-semibold text-lg px-4 text-gray-900 dark:text-white">
                {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <Button onClick={() => navigateMonth(1)} variant="outline" size="sm">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : calendarData ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Calendar Grid */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
                    const lastDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
                    const days = [];
                    
                    // Add empty cells for days before first of month
                    for (let i = 0; i < firstDay.getDay(); i++) {
                      days.push(<div key={`empty-${i}`} className="h-24" />);
                    }
                    
                    // Add day cells
                    for (let d = 1; d <= lastDay.getDate(); d++) {
                      const dateStr = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                      const events = calendarData.events_by_date?.[dateStr] || [];
                      const isToday = new Date().toDateString() === new Date(dateStr).toDateString();
                      
                      days.push(
                        <div
                          key={d}
                          onClick={() => events.length > 0 && setSelectedDateOrders({ date: dateStr, orders: events })}
                          className={`h-24 border rounded-lg p-1 transition-all cursor-pointer ${
                            isToday ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700'
                          } ${events.length > 0 ? 'hover:border-amber-400 hover:shadow' : ''}`}
                        >
                          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-amber-600' : 'text-gray-700 dark:text-gray-300'}`}>
                            {d}
                          </div>
                          {events.length > 0 && (
                            <div className="space-y-0.5">
                              {events.slice(0, 2).map((event, idx) => (
                                <div
                                  key={idx}
                                  className={`text-xs px-1 py-0.5 rounded truncate ${
                                    event.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                    event.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    event.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                                    'bg-green-100 text-green-700'
                                  }`}
                                  title={event.company_name || event.client_name}
                                >
                                  {event.event_time} - {event.company_name || event.client_name}
                                </div>
                              ))}
                              {events.length > 2 && (
                                <div className="text-xs text-gray-500 pl-1">+{events.length - 2} more</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    return days;
                  })()}
                </div>
                
                {/* Legend */}
                <div className="mt-4 flex gap-4 flex-wrap text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></span>
                    Pending
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span>
                    Confirmed
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span>
                    Ready/Delivered
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-gray-100 border border-gray-300"></span>
                    Completed
                  </div>
                </div>
              </div>
              
              {/* Selected Date Details / Summary */}
              <div className="space-y-4">
                {/* Month Summary */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Month Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Events</span>
                      <span className="font-medium text-gray-900 dark:text-white">{calendarData.summary?.total_events || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Expected Revenue</span>
                      <span className="font-bold text-amber-600">${(calendarData.summary?.total_revenue || 0).toLocaleString()}</span>
                    </div>
                    {calendarData.summary?.status_counts && Object.entries(calendarData.summary.status_counts).map(([status, count]) => (
                      <div key={status} className="flex justify-between">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[status]}`}>{status}</span>
                        <span className="text-gray-600 dark:text-gray-400">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Selected Date Orders */}
                {selectedDateOrders && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {new Date(selectedDateOrders.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                      </h3>
                      <button onClick={() => setSelectedDateOrders(null)} className="text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedDateOrders.orders.map((order, idx) => (
                        <div key={idx} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{order.company_name || order.client_name}</p>
                              <p className="text-xs text-gray-500">{order.event_time} • {order.guest_count} guests</p>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[order.status]}`}>
                              {order.status}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">{order.occasion}</span>
                            <span className="font-bold text-amber-600">${order.total?.toFixed(2)}</span>
                          </div>
                          {order.deposit_status && (
                            <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${DEPOSIT_STATUS_COLORS[order.deposit_status]}`}>
                                Deposit: {order.deposit_status}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-12">Failed to load calendar</p>
          )}
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === 'payments' && (
        <div className="space-y-6">
          {/* Sub-tabs: Payment Proofs / Corporate Credits */}
          <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setPaymentFilter('pending_verification')}
              className={`pb-2 px-1 font-medium border-b-2 transition-colors ${
                paymentFilter === 'pending_verification'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Pending Verification ({paymentProofs.filter(p => p.status === 'pending_verification').length})
            </button>
            <button
              onClick={() => setPaymentFilter('verified')}
              className={`pb-2 px-1 font-medium border-b-2 transition-colors ${
                paymentFilter === 'verified'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Verified
            </button>
            <button
              onClick={() => setPaymentFilter('all')}
              className={`pb-2 px-1 font-medium border-b-2 transition-colors ${
                paymentFilter === 'all'
                  ? 'border-gray-600 text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
          </div>

          {/* Payment Proofs Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 dark:text-white">M-PAiSA Payment Proofs</h3>
              <Button 
                size="sm" 
                variant="outline"
                onClick={fetchPaymentProofs}
              >
                Refresh
              </Button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              </div>
            ) : paymentProofs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No payment proofs {paymentFilter !== 'all' ? 'with this status' : 'yet'}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {paymentProofs.map(proof => (
                  <div key={proof.proof_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {proof.order_info?.company_name || 'Unknown Company'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            proof.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-700' :
                            proof.status === 'verified' ? 'bg-green-100 text-green-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {proof.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          Order: {proof.order_id?.slice(-8)} | Ref: {proof.transaction_ref} | Amount: <span className="font-semibold text-amber-600">${proof.amount_claimed?.toFixed(2)}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Phone: {proof.payer_phone} | Submitted: {new Date(proof.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedProof(proof);
                            fetchProofImage(proof.proof_id);
                          }}
                        >
                          View Proof
                        </Button>
                        {proof.status === 'pending_verification' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => verifyPaymentProof(proof.proof_id, true)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Verify
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              onClick={() => verifyPaymentProof(proof.proof_id, false, 'Rejected by admin')}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Corporate Credits Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 dark:text-white">Corporate Credit Terms</h3>
              <Button 
                size="sm" 
                onClick={() => setShowCreditForm(!showCreditForm)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Credit Terms
              </Button>
            </div>
            
            {/* Add Credit Form */}
            {showCreditForm && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Client ID *</label>
                    <select
                      value={creditForm.client_id}
                      onChange={(e) => {
                        const client = clients.find(c => c.contact_id === e.target.value);
                        setCreditForm({
                          ...creditForm,
                          client_id: e.target.value,
                          company_name: client?.company_name || ''
                        });
                      }}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2"
                    >
                      <option value="">Select client...</option>
                      {clients.map(c => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.company_name || c.full_name} ({c.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Credit Limit ($) *</label>
                    <Input
                      type="number"
                      value={creditForm.credit_limit}
                      onChange={(e) => setCreditForm({...creditForm, credit_limit: e.target.value})}
                      placeholder="5000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Terms</label>
                    <select
                      value={creditForm.payment_terms}
                      onChange={(e) => setCreditForm({...creditForm, payment_terms: e.target.value})}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2"
                    >
                      <option value="net_7">Net 7 Days</option>
                      <option value="net_14">Net 14 Days</option>
                      <option value="net_30">Net 30 Days</option>
                      <option value="net_60">Net 60 Days</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Deposit %</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={creditForm.deposit_percentage}
                      onChange={(e) => setCreditForm({...creditForm, deposit_percentage: parseInt(e.target.value)})}
                    />
                  </div>
                  <label className="flex items-center gap-2 pt-6">
                    <input
                      type="checkbox"
                      checked={creditForm.deposit_waiver}
                      onChange={(e) => setCreditForm({...creditForm, deposit_waiver: e.target.checked, deposit_percentage: e.target.checked ? 0 : 30})}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Waive Deposit Entirely</span>
                  </label>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason / Notes</label>
                  <Input
                    value={creditForm.reason}
                    onChange={(e) => setCreditForm({...creditForm, reason: e.target.value})}
                    placeholder="e.g., Long-standing corporate client"
                  />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={saveCorporateCredit}>
                    <Save className="w-4 h-4 mr-1" />
                    Save Credit Terms
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreditForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            
            {/* Credits List */}
            {corporateCredits.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Banknote className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No corporate credit terms configured</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Company</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Credit Limit</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Terms</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Deposit</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {corporateCredits.map(credit => (
                    <tr key={credit.client_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{credit.company_name}</div>
                        <div className="text-xs text-gray-500">Approved by: {credit.approved_by}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-amber-600">
                        ${credit.credit_limit?.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                        {credit.payment_terms?.replace(/_/g, ' ').toUpperCase()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {credit.deposit_waiver ? (
                          <span className="text-green-600 font-medium">Waived</span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">{credit.deposit_percentage}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          credit.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {credit.is_active ? 'Active' : 'Revoked'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {credit.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => revokeCorporateCredit(credit.client_id)}
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Payment Proof Image Modal */}
          {selectedProof && proofImageData && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => { setSelectedProof(null); setProofImageData(null); }}>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Payment Proof - {selectedProof.transaction_ref}</h3>
                  <button onClick={() => { setSelectedProof(null); setProofImageData(null); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  <div className="mb-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Amount Claimed:</span>
                      <span className="ml-2 font-semibold text-amber-600">${selectedProof.amount_claimed?.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Payer Phone:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{selectedProof.payer_phone}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Order Deposit Required:</span>
                      <span className="ml-2 font-semibold text-gray-900 dark:text-white">${selectedProof.order_info?.deposit_required?.toFixed(2) || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Order Total:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">${selectedProof.order_info?.gross_total?.toFixed(2) || 'N/A'}</span>
                    </div>
                  </div>
                  <img 
                    src={`data:${proofImageData.image_type};base64,${proofImageData.image_data}`}
                    alt="Payment proof"
                    className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                  {selectedProof.notes && (
                    <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      <strong>Notes:</strong> {selectedProof.notes}
                    </p>
                  )}
                </div>
                {selectedProof.status === 'pending_verification' && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
                    <Button
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => verifyPaymentProof(selectedProof.proof_id, true)}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Verify Payment
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => verifyPaymentProof(selectedProof.proof_id, false, 'Rejected after review')}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Clients Tab */}
      {activeTab === 'clients' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="search"
              placeholder="Search clients..."
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              className="pl-10"
              data-testid="catering-client-search"
            />
          </div>

          {/* Clients List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No clients found</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Company</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Contact</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Phone</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">Orders</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Total Spent</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredClients.map(client => (
                    <tr key={client.contact_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{client.company_name || '-'}</div>
                        {client.position && <div className="text-xs text-gray-500">{client.position}</div>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{client.full_name}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{client.email}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{client.phone}</td>
                      <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">{client.order_count || 0}</td>
                      <td className="px-4 py-3 text-right font-medium text-amber-600">
                        ${(client.total_spent || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
